import { fetchLandingLinks } from '@/api/landings';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { ILandingLink, ILandingLinkCreated } from '@/entities/landing.entity';
import { formatMetaPixelOptionLabel, formatLandingDownloadTriggerLabel } from '@/entities/landing.entity';
import { PageScroll } from '@/layouts/page-layout';
import CreateLinkModal from '@/pages/landings/components/create-link-modal';
import CreatedLinkDialog from '@/pages/landings/components/created-link-dialog';
import EditLinkModal from '@/pages/landings/components/edit-link-modal';
import LandingsEmptyState from '@/pages/landings/components/landings-empty-state';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import LandingsPageCard from '@/pages/landings/components/landings-page-card';
import { hostnameFromLandingUrl } from '@/pages/landings/lib/landing-url';
import LandingsGate from '@/pages/landings/landings-gate';
import { CopyIcon, ExternalLinkIcon, LinkIcon, PlusIcon, Settings2Icon } from 'lucide-react';
import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import { toast } from 'sonner';

function formatLinksCount(count: number): string {
	if (count === 0) return 'Нет ссылок';
	const word = count === 1 ? 'ссылка' : count < 5 ? 'ссылки' : 'ссылок';
	return `${count} ${word}`;
}

export default function LandingsLinksPage() {
	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<LandingsGate>
				<LinksContent />
			</LandingsGate>
		</PageScroll>
	);
}

function LinksContent() {
	const [links, setLinks] = useState<ILandingLink[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [createOpen, setCreateOpen] = useState(false);
	const [createdLink, setCreatedLink] = useState<ILandingLinkCreated | null>(null);
	const [editingLink, setEditingLink] = useState<ILandingLink | null>(null);

	const loadLinks = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		setError(false);
		try {
			const data = await fetchLandingLinks({ limit: 50 });
			setLinks(data.items);
		} catch {
			setError(true);
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadLinks(true);
	}, [loadLinks]);

	function handleCreated(link: ILandingLinkCreated) {
		setCreatedLink(link);
		void loadLinks();
	}

	return (
		<>
			<LandingsPageCard
				icon={LinkIcon}
				title='Ссылки'
				description={formatLinksCount(links.length)}
				color='chart-5'
				action={
					<Button type='button' size='sm' onClick={() => setCreateOpen(true)}>
						<PlusIcon size={16} className='mr-1' />
						Создать
					</Button>
				}
			>
				{loading ? (
					<div className='flex justify-center py-8'>
						<Spinner />
					</div>
				) : error ? (
					<LandingsErrorState onRetry={() => void loadLinks(true)} />
				) : links.length === 0 ? (
					<LandingsEmptyState
						icon={LinkIcon}
						title='Ссылок пока нет'
						description='Создайте ссылку для трекинга трафика на сайт лендинга.'
						actionLabel='Создать ссылку'
						onAction={() => setCreateOpen(true)}
					/>
				) : (
					<div className='flex flex-col gap-3'>
						{links.map(link => (
							<LinkRow key={link.id} link={link} onEdit={() => setEditingLink(link)} />
						))}
					</div>
				)}
			</LandingsPageCard>

			<CreateLinkModal open={createOpen} onOpenChange={setCreateOpen} onCreated={handleCreated} />
			<CreatedLinkDialog link={createdLink} onClose={() => setCreatedLink(null)} />
			<EditLinkModal
				link={editingLink}
				onOpenChange={open => {
					if (!open) setEditingLink(null);
				}}
				onUpdated={() => void loadLinks()}
			/>
		</>
	);
}

function LinkRow({ link, onEdit }: { link: ILandingLink; onEdit: () => void }) {
	const hostname = hostnameFromLandingUrl(link.url);

	function copyUrl(e: MouseEvent) {
		e.stopPropagation();
		navigator.clipboard
			.writeText(link.url)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	return (
		<div className='rounded-xl border border-border bg-card p-3 shadow-sm'>
			<div className='flex items-start justify-between gap-2'>
				<div className='min-w-0 flex-1'>
					<p className='font-medium truncate'>{hostname}</p>
					<p className='text-xs text-muted-foreground mt-0.5 font-mono'>{link.id}</p>
					<div className='mt-1.5 flex flex-wrap items-center gap-2'>
						<span className='inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground'>
							{link.templateSlug}
						</span>
						{link.metaPixel ? (
							<span className='inline-flex items-center rounded-md bg-chart-4/15 px-2 py-0.5 text-xs text-chart-4'>
								{formatMetaPixelOptionLabel({
									name: link.metaPixel.name,
									conversionEvent: link.metaPixel.conversionEvent,
								})}
							</span>
						) : null}
						{link.downloadTrigger && link.downloadTrigger !== 'cta' ? (
							<span
								className='inline-flex items-center rounded-md bg-chart-5/15 px-2 py-0.5 text-xs text-chart-5 max-w-[220px] truncate'
								title={formatLandingDownloadTriggerLabel(link.downloadTrigger)}
							>
								{formatLandingDownloadTriggerLabel(link.downloadTrigger)}
							</span>
						) : null}
						<span className='text-xs text-muted-foreground truncate max-w-[140px]' title={link.buildId ?? undefined}>
							{link.buildId}
						</span>
					</div>
				</div>
				<div className='flex gap-1 shrink-0'>
					<Button type='button' variant='ghost' size='icon' className='size-8' onClick={onEdit}>
						<Settings2Icon size={14} />
					</Button>
					<Button type='button' variant='ghost' size='icon' className='size-8' onClick={copyUrl}>
						<CopyIcon size={14} />
					</Button>
					<Button type='button' variant='ghost' size='icon' className='size-8' asChild>
						<a href={link.url} target='_blank' rel='noopener noreferrer'>
							<ExternalLinkIcon size={14} />
						</a>
					</Button>
				</div>
			</div>
		</div>
	);
}
